// Cliente da API do CScan. A mesma interface roda em dois lugares e fala
// sempre com a própria origem: dentro do aplicativo (motor local, offline) e
// no painel online (nuvem). Em dev, o Vite faz proxy para 127.0.0.1:8787.

// 'owner' é o dono da nuvem: só cadastra clínicas, não acessa dado clínico.
export type Role = 'admin' | 'medico' | 'tecnico' | 'recepcao' | 'owner'

// Onde esta interface está rodando.
export interface Env {
  mode: 'node' | 'hub' // aplicativo no computador | painel online
  node: string
  clinic?: string // clínica deste computador, depois do primeiro login
}

export interface Clinic {
  id: string
  name: string
  active: boolean
  createdAt: string
  devices: number
  lastSeenAt?: string
}

export interface ClinicDevice {
  id: string
  name: string
  platform: string
  appVersion: string
  enrolledBy: string
  createdAt: string
  lastSeenAt?: string
  revokedAt?: string
}

export interface Installer {
  name: string
  platform: 'windows' | 'macos' | 'outro'
  size: number
  url: string
}

export interface User {
  id: string
  login: string
  name: string
  email?: string
  role: Role
  active: boolean
  professionalId?: string
}

export interface Patient {
  id: string
  name: string
  birthDate?: string
  sex?: string
  doc?: string
  cpf?: string
  phone?: string
  email?: string
  address?: string
  insurance?: string
  insuranceNumber?: string
  notes?: string
  allergies?: string
  createdAt: string
  updatedAt: string
}

export interface Media {
  id: string
  sha256: string
  mime: string
  size: number
  width?: number
  height?: number
  durationMs?: number
  createdAt: string
}

export interface Appointment {
  id: string
  startsAt: string
  patientId: string
  examType: string
  professionalId?: string
  insuranceId?: string
  status: string
  notes?: string
  patientName?: string
  professionalName?: string
  insuranceName?: string
}

export interface Exam {
  id: string
  appointmentId?: string
  patientId: string
  type: string
  professionalId?: string
  equipmentId?: string
  insuranceId?: string
  templateId?: string
  startedAt?: string
  finishedAt?: string
  status: string
  notes?: string
}

export interface MediaLink {
  id: string
  mediaId: string
  entity: string
  entityId: string
  kind: string
  caption?: string
  position: number
  inReport?: boolean
  createdAt?: string
}

// O que dá para escolher num aparelho de captura deste computador.
export interface CaptureOptions {
  inputs: { pin: string; name: string; current: boolean }[]
  input: string
  has4k: boolean
  quality: 'fullhd' | '4k'
  configurable: boolean
}

export interface ExamMedia {
  link: MediaLink
  media: Media
}

export interface TemplateTopic {
  id: string
  title: string
  default?: string
}

export interface ReportTemplate {
  id: string
  name: string
  printTitle: string
  equipment?: string
  topics: TemplateTopic[]
  active: boolean
}

export interface Phrase {
  id: string
  topic: string
  text: string
  favorite: boolean
}

export interface Report {
  id: string
  examId: string
  patientId: string
  number: string
  title: string
  model: string
  templateId?: string
  content: Record<string, string>
  status: 'rascunho' | 'liberado'
  releasedAt?: string
  releasedBy?: string
  professionalId?: string
  examDate?: string
  createdAt: string
  updatedAt: string
}

export interface ReportSummary extends Report {
  patientName: string
  patientBirth: string
  patientDoc: string
  patientSex: string
  professionalName: string
  insurance: string
}

export interface ReportDetail {
  clinic: { name: string; lines: string[]; logoSha?: string } // cabeçalho impresso (igual ao do PDF)
  report: Report
  patient?: Patient
  exam?: Exam
  template?: ReportTemplate
  examiner?: Record<string, unknown>
  equipment?: string // aparelho impresso no laudo
  media: ExamMedia[]
  versions?: { version: number; hash: string; createdAt: string }[]
}

export interface ReportStats {
  total: number
  released: number
  draft: number
  byType: { label: string; value: number }[]
  bySex: { label: string; value: number }[]
  byInsurance: { label: string; value: number }[]
  byEquipment: { label: string; value: number }[]
  byDay: { label: string; value: number }[]
}

export interface Prescription {
  id: string
  examId?: string
  patientId: string
  professionalId?: string
  content: string
  guidance: string
  createdAt: string
  updatedAt: string
}

export interface SyncStatus {
  mode: string
  node: string
  hub: boolean
  pendingChanges?: number
  pendingMedia?: number
  [key: string]: unknown
}

// O que é preciso para enviar um laudo ao paciente.
export interface ShareInfo {
  url: string
  patient: string
  phone: string
  email: string
  title: string
  clinic: string
}

// Dados da clínica que saem no cabeçalho do laudo.
export interface ClinicProfile {
  name: string
  address: string
  phone: string
  logoSha: string
}

export interface Equipment {
  id: string
  description: string
  brand?: string
  model?: string
  serial?: string
}

export interface CatalogRow {
  id: string
  [key: string]: unknown
}

export interface Professional {
  id: string
  name: string
  crm?: string
  crm_uf?: string
  specialty?: string
  email?: string
  phone?: string
  roles?: string
}

const signedCache = new Map<string, { url: string; exp: number }>()

const TOKEN_KEY = 'cscan.token'

export function getToken(): string {
  return localStorage.getItem(TOKEN_KEY) ?? ''
}
export function setToken(token: string) {
  if (token) localStorage.setItem(TOKEN_KEY, token)
  else localStorage.removeItem(TOKEN_KEY)
}

export class ApiError extends Error {
  status: number
  constructor(status: number, message: string) {
    super(message)
    this.status = status
  }
}

let onUnauthorized: (() => void) | null = null
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {}
  const token = getToken()
  if (token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const res = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })

  if (res.status === 401) {
    setToken('')
    onUnauthorized?.()
    throw new ApiError(401, 'sessão expirada')
  }
  if (!res.ok) {
    let msg = `erro ${res.status}`
    try {
      const data = (await res.json()) as { error?: string }
      if (data.error) msg = data.error
    } catch {
      /* corpo não-JSON */
    }
    throw new ApiError(res.status, msg)
  }
  if (res.status === 204) return undefined as T
  return (await res.json()) as T
}

async function uploadBlob(path: string, blob: Blob): Promise<Media> {
  const res = await fetch(path, {
    method: 'POST',
    headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/octet-stream' },
    body: blob,
  })
  if (!res.ok) throw new ApiError(res.status, `falha no upload (${res.status})`)
  return (await res.json()) as Media
}

// Grava a versão editada de uma foto do exame. A original não é alterada:
// a cópia editada entra ao lado dela, com a mesma legenda, e é a cópia que
// passa a ir para o laudo.
export async function saveEditedImage(
  examId: string,
  original: { linkId: string; position: number; caption?: string },
  blob: Blob, width: number, height: number,
): Promise<void> {
  const media = await api.media.upload(blob, { mime: 'image/jpeg', width, height })
  await api.exams.linkMedia(examId, { mediaId: media.id, kind: 'photo', caption: original.caption, position: original.position })
  await api.exams.updateLink(original.linkId, { inReport: false })
}

export const api = {
  async login(login: string, password: string): Promise<{ user: User; via: 'online' | 'offline' }> {
    const out = await request<{ token: string; expiresAt: string; user: User; via?: string }>(
      'POST', '/api/v1/auth/login', { login, password },
    )
    setToken(out.token)
    return { user: out.user, via: out.via === 'offline' ? 'offline' : 'online' }
  },
  me: () => request<User>('GET', '/api/v1/auth/me'),
  env: async (): Promise<Env> => {
    const res = await fetch('/healthz')
    const d = (await res.json()) as { mode?: string; node?: string; clinic?: string }
    return { mode: d.mode === 'hub' ? 'hub' : 'node', node: d.node ?? '', clinic: d.clinic }
  },
  // Abre um endereço de envio (WhatsApp, e-mail) no programa padrão: pelo
  // motor, no aplicativo; numa aba nova, no painel online.
  openLink: async (url: string) => {
    try {
      const res = await fetch('/api/v1/open-link', {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      })
      if (res.ok) return
    } catch {
      // segue para a aba nova
    }
    window.open(url, '_blank', 'noopener')
  },
  // Instaladores do aplicativo publicados na nuvem (lista vazia no aplicativo).
  installers: async (): Promise<Installer[]> => {
    try {
      const res = await fetch('/api/v1/downloads')
      if (!res.ok) return []
      return ((await res.json()) as { items: Installer[] }).items
    } catch {
      return []
    }
  },

  // Painel do dono da nuvem.
  owner: {
    clinics: () => request<{ items: Clinic[] }>('GET', '/api/v1/owner/clinics'),
    createClinic: (c: { name: string; adminName: string; adminLogin: string; adminPassword: string }) =>
      request<Clinic>('POST', '/api/v1/owner/clinics', c),
    updateClinic: (id: string, patch: { name?: string; active?: boolean }) =>
      request<Clinic>('PATCH', `/api/v1/owner/clinics/${id}`, patch),
    devices: (clinicId: string) =>
      request<{ items: ClinicDevice[] }>('GET', `/api/v1/owner/clinics/${clinicId}/devices`),
    setDeviceRevoked: (deviceId: string, revoked: boolean) =>
      request<ClinicDevice>('PATCH', `/api/v1/owner/devices/${encodeURIComponent(deviceId)}`, { revoked }),
  },

  patients: {
    list: (q = '', limit = 50, offset = 0) =>
      request<{ items: Patient[]; total: number }>(
        'GET', `/api/v1/patients?q=${encodeURIComponent(q)}&limit=${limit}&offset=${offset}`,
      ),
    create: (p: Partial<Patient>) => request<Patient>('POST', '/api/v1/patients', p),
    update: (p: Patient) => request<Patient>('PATCH', `/api/v1/patients/${p.id}`, p),
    remove: (id: string) => request<void>('DELETE', `/api/v1/patients/${id}`),
    exams: (id: string) => request<{ items: Exam[] }>('GET', `/api/v1/patients/${id}/exams`),
    prescriptions: (id: string) =>
      request<{ items: Prescription[] }>('GET', `/api/v1/patients/${id}/prescriptions`),
  },

  appointments: {
    list: (date: string) =>
      request<{ date: string; items: Appointment[] }>('GET', `/api/v1/appointments?date=${date}`),
    create: (a: Partial<Appointment>) => request<Appointment>('POST', '/api/v1/appointments', a),
    update: (a: Appointment) => request<Appointment>('PATCH', `/api/v1/appointments/${a.id}`, a),
  },

  exams: {
    create: (e: Partial<Exam>) => request<{ exam: Exam; media: ExamMedia[] }>('POST', '/api/v1/exams', e),
    get: (id: string) => request<{ exam: Exam; media: ExamMedia[] }>('GET', `/api/v1/exams/${id}`),
    update: (e: Exam) => request<Exam>('PATCH', `/api/v1/exams/${e.id}`, e),
    linkMedia: (examId: string, body: { mediaId: string; kind?: string; caption?: string; position?: number }) =>
      request<MediaLink>('POST', `/api/v1/exams/${examId}/media`, body),
    updateLink: (linkId: string, body: { caption?: string; position?: number; inReport?: boolean }) =>
      request<MediaLink>('PATCH', `/api/v1/media-links/${linkId}`, body),
    unlink: (linkId: string) => request<void>('DELETE', `/api/v1/media-links/${linkId}`),
  },

  media: {
    upload: (blob: Blob, meta: { mime?: string; width?: number; height?: number; durationMs?: number } = {}) => {
      const params = new URLSearchParams()
      params.set('mime', meta.mime ?? blob.type ?? 'application/octet-stream')
      if (meta.width) params.set('width', String(meta.width))
      if (meta.height) params.set('height', String(meta.height))
      if (meta.durationMs) params.set('durationMs', String(meta.durationMs))
      return uploadBlob(`/api/v1/media?${params}`, blob)
    },
    // URL assinada e temporária para uso em <img>/<video> (que não enviam
    // o header Authorization). Cacheada até pouco antes de expirar.
    signedUrl: async (sha: string) => {
      const hit = signedCache.get(sha)
      const now = Math.floor(Date.now() / 1000)
      if (hit && hit.exp > now + 60) return hit.url
      const out = await request<{ url: string; expiresAt: string }>('GET', `/api/v1/media/${sha}/url`)
      const exp = Math.floor(new Date(out.expiresAt).getTime() / 1000)
      signedCache.set(sha, { url: out.url, exp })
      return out.url
    },
  },

  capture: {
    // Dispositivos nativos do sistema (aparecem mesmo quando não são UVC).
    devices: () => request<{ available: boolean; items: { id: string; name: string }[] }>('GET', '/api/v1/capture/devices'),
    previewUrl: (device: string) =>
      request<{ url: string }>('GET', `/api/v1/capture/preview/url?device=${encodeURIComponent(device)}`),
    // Avisa que a tela saiu do vídeo ao vivo: o motor encerra o stream e libera a câmera.
    closePreview: (viewer: string) => {
      if (!viewer) return
      void fetch('/api/v1/capture/preview/close', {
        method: 'POST',
        keepalive: true,
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ viewer }),
      }).catch(() => undefined)
    },
    options: (device: string) =>
      request<CaptureOptions>('GET', `/api/v1/capture/options?device=${encodeURIComponent(device)}`),
    setOptions: (device: string, patch: { input?: string; quality?: string }) =>
      request<void>('PUT', '/api/v1/capture/options', { device, ...patch }),
    // Abre a janela de ajustes do driver; só responde quando ela é fechada.
    configure: (device: string) => request<void>('POST', '/api/v1/capture/configure', { device }),
    photo: (device: string, width = 1280, height = 720) =>
      request<Media>('POST', '/api/v1/capture/photo', { device, width, height }),
    recordStart: (device: string, width = 1280, height = 720) =>
      request<{ status: string }>('POST', '/api/v1/capture/record/start', { device, width, height }),
    recordStop: () => request<Media>('POST', '/api/v1/capture/record/stop'),
  },

  templates: {
    list: (all = false) =>
      request<{ items: ReportTemplate[] }>('GET', `/api/v1/report-templates${all ? '?all=1' : ''}`),
    create: (t: Partial<ReportTemplate>) => request<ReportTemplate>('POST', '/api/v1/report-templates', t),
    update: (t: ReportTemplate) => request<ReportTemplate>('PATCH', `/api/v1/report-templates/${t.id}`, t),
  },

  phrases: {
    list: (topic?: string) =>
      request<{ items: Phrase[] }>('GET', `/api/v1/phrases${topic ? `?topic=${encodeURIComponent(topic)}` : ''}`),
    create: (p: { topic: string; text: string; favorite?: boolean }) =>
      request<Phrase>('POST', '/api/v1/phrases', p),
    update: (p: Phrase) => request<Phrase>('PATCH', `/api/v1/phrases/${p.id}`, p),
  },

  reports: {
    list: (params: Record<string, string>) => {
      const qs = new URLSearchParams(params).toString()
      return request<{ items: ReportSummary[]; total: number }>('GET', `/api/v1/reports?${qs}`)
    },
    create: (examId: string, templateId?: string) =>
      request<ReportDetail>('POST', '/api/v1/reports', { examId, templateId }),
    get: (id: string) => request<ReportDetail>('GET', `/api/v1/reports/${id}`),
    save: (id: string, patch: Partial<Report>) => request<ReportDetail>('PATCH', `/api/v1/reports/${id}`, patch),
    // Salva ao sair da tela (troca de aba, fechar a janela): o pedido segue
    // mesmo com a tela já desmontada.
    saveOnExit: (id: string, patch: Partial<Report>) => {
      void fetch(`/api/v1/reports/${id}`, {
        method: 'PATCH',
        keepalive: true,
        headers: { Authorization: `Bearer ${getToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      }).catch(() => undefined)
    },
    release: (id: string) => request<ReportDetail>('POST', `/api/v1/reports/${id}/release`),
    reopen: (id: string) => request<ReportDetail>('POST', `/api/v1/reports/${id}/reopen`),
    stats: (from?: string, to?: string) =>
      request<ReportStats>('GET', `/api/v1/reports/stats?from=${from ?? ''}&to=${to ?? ''}`),
    // Prepara o envio do laudo liberado ao paciente: endereço e contatos.
    share: (id: string) => request<ShareInfo>('POST', `/api/v1/reports/${id}/share`),
    // Abre o PDF no visualizador do sistema (o servidor local grava e abre).
    // Se o servidor não puder abrir (ex.: acesso remoto), baixa o arquivo.
    openPdf: async (id: string) => {
      try {
        const res = await fetch(`/api/v1/reports/${id}/pdf/open`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${getToken()}` },
        })
        if (res.ok) return
      } catch {
        // segue para o download
      }
      const res = await fetch(`/api/v1/reports/${id}/pdf`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      })
      if (!res.ok) throw new ApiError(res.status, 'falha ao gerar PDF')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `laudo-${id.slice(0, 8)}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    },
  },

  prescriptions: {
    create: (p: Partial<Prescription>) => request<Prescription>('POST', '/api/v1/prescriptions', p),
    get: (id: string) => request<Prescription>('GET', `/api/v1/prescriptions/${id}`),
    update: (p: Prescription) => request<Prescription>('PATCH', `/api/v1/prescriptions/${p.id}`, p),
  },

  clinic: {
    get: () => request<ClinicProfile>('GET', '/api/v1/clinic'),
    save: (p: { address: string; phone: string; logoSha: string }) => request<ClinicProfile>('PUT', '/api/v1/clinic', p),
  },

  catalog: {
    list: (table: string) => request<{ items: CatalogRow[] }>('GET', `/api/v1/catalog/${table}`),
    professionals: () => request<{ items: Professional[] }>('GET', '/api/v1/catalog/professionals'),
    equipments: () => request<{ items: Equipment[] }>('GET', '/api/v1/catalog/equipments'),
    upsert: (table: string, payload: Record<string, unknown>) =>
      request<CatalogRow>('POST', `/api/v1/catalog/${table}`, payload),
    remove: (table: string, id: string) => request<void>('DELETE', `/api/v1/catalog/${table}/${id}`),
  },

  sync: {
    status: () => request<SyncStatus>('GET', '/api/v1/sync/status'),
    now: () => request<{ status: string }>('POST', '/api/v1/sync/now'),
    requeue: () => request<{ requeued: number }>('POST', '/api/v1/sync/requeue'),
  },

  users: {
    list: () => request<{ items: User[] }>('GET', '/api/v1/users'),
    create: (u: { login: string; name: string; role: string; password: string; professionalId?: string }) =>
      request<User>('POST', '/api/v1/users', u),
    update: (id: string, patch: Record<string, unknown>) =>
      request<User>('PATCH', `/api/v1/users/${id}`, patch),
  },
}

// Idade na data informada (a do exame, nos laudos) ou hoje.
export function formatAge(birth?: string, at?: string): string {
  if (!birth || birth.length < 10) return '—'
  const t = new Date(birth + 'T00:00:00')
  if (isNaN(t.getTime())) return '—'
  const ref = at && at.length >= 10 ? new Date(at.slice(0, 10) + 'T00:00:00') : new Date()
  const now = isNaN(ref.getTime()) ? new Date() : ref
  let age = now.getFullYear() - t.getFullYear()
  const m = now.getMonth() - t.getMonth()
  if (m < 0 || (m === 0 && now.getDate() < t.getDate())) age--
  return age >= 0 && age < 130 ? `${age} anos` : '—'
}

export function formatDateBR(date?: string): string {
  if (!date || date.length < 10) return date ?? ''
  const [y, m, d] = date.slice(0, 10).split('-')
  return `${d}/${m}/${y}`
}

export function formatDateTimeBR(stamp?: string): string {
  if (!stamp) return ''
  const t = new Date(stamp)
  if (isNaN(t.getTime())) return stamp
  return t.toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

export function todayISO(): string {
  const d = new Date()
  const p = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}
